type AuditActorFrame = {
  actor: string;
  source: string;
};

type AuditSample = {
  wallMs: number;
  audioMs: number;
  audioSource: string;
  caption: string;
  frames: AuditActorFrame[];
};

type AuditEvent = {
  type: string;
  wallMs: number;
  audioMs?: number;
  source?: string;
  actor?: string;
  caption?: string;
};

type AuditState = {
  schemaVersion: "1.0.0";
  pageUrl: string;
  startedAt: string;
  events: AuditEvent[];
  samples: AuditSample[];
};

type AlignmentReceipt = {
  audioFileName: string;
  audioSha256: string;
  transcript: string;
  durationSeconds: number;
  loss: number;
  words: Array<{
    text: string;
    start: number;
    end: number;
  }>;
};

type PlaybackSession = {
  source: string;
  startedWallMs: number;
  eventStartIndex: number;
  initialFrames: AuditActorFrame[];
  receipt: Promise<AlignmentReceipt | null>;
};

const enabled = new URLSearchParams(window.location.search).get("qaPlayback") === "1";

if (enabled) {
  const state: AuditState = {
    schemaVersion: "1.0.0",
    pageUrl: window.location.href,
    startedAt: new Date().toISOString(),
    events: [],
    samples: [],
  };
  const sink = document.createElement("script");
  sink.id = "awkwardpixels-playback-audit";
  sink.type = "application/json";
  document.head.appendChild(sink);
  const startedAt = performance.now();
  const activeMedia = new Set<HTMLMediaElement>();
  const playbackSessions = new WeakMap<HTMLMediaElement, PlaybackSession>();
  const frameByCanvas = new Map<HTMLCanvasElement, string>();
  let caption = "";
  let publishQueued = false;

  const wallMs = (): number => Math.round(performance.now() - startedAt);
  const playingMedia = (): HTMLMediaElement | undefined =>
    Array.from(activeMedia).find((media) => !media.paused && !media.ended);
  const publish = (): void => {
    if (publishQueued) return;
    publishQueued = true;
    window.setTimeout(() => {
      sink.textContent = JSON.stringify(state);
      publishQueued = false;
    }, 25);
  };
  const addEvent = (event: AuditEvent): void => {
    state.events.push(event);
    if (state.events.length > 5000) state.events.shift();
    publish();
  };

  const fileName = (source: string): string => {
    const path = source.split("?")[0];
    return path.split("/").pop() || "unknown-audio";
  };
  const escapeXml = (value: string): string => value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&apos;");
  const loadReceipt = async (source: string): Promise<AlignmentReceipt | null> => {
    const stem = fileName(source).replace(/\.[^.]+$/, "");
    try {
      const response = await fetch(`/sprites/lip-sync-qa/alignments/${encodeURIComponent(stem)}.json`, {
        cache: "no-store",
      });
      if (!response.ok) return null;
      const receipt = await response.json() as AlignmentReceipt;
      return receipt.words?.length ? receipt : null;
    } catch {
      return null;
    }
  };
  const parseCell = (source: string): {
    appearance: string;
    pose: string;
    mouth: string;
    blink: string;
  } => {
    const parts = fileName(source).replace(/\.png$/i, "").split(".");
    return {
      appearance: parts[0] || "unknown",
      pose: parts[1] || "unknown",
      mouth: parts[2] || "unknown",
      blink: parts[3] || "unknown",
    };
  };
  const mouthColor = (mouth: string): string => ({
    rest: "#23314f",
    aa: "#56e0cf",
    smile: "#ff77a8",
    fv: "#ffd166",
    o: "#75a7ff",
  })[mouth] || "#79839d";
  const shortActor = (actor: string): string =>
    actor.toLowerCase().includes("winston") ? "WINSTON" :
      actor.toLowerCase().includes("asher") ? "ASHER" : actor.slice(0, 16).toUpperCase();

  const renderPlaybackReport = async (
    media: HTMLMediaElement,
    session: PlaybackSession,
  ): Promise<void> => {
    const receipt = await session.receipt;
    if (!receipt) return;
    const endedWallMs = wallMs();
    const durationMs = Math.max(
      1,
      Math.round((Number.isFinite(media.duration) ? media.duration : receipt.durationSeconds) * 1000),
    );
    const events = state.events.slice(session.eventStartIndex).filter((event) =>
      event.wallMs <= endedWallMs && event.type === "frame" && event.actor && event.source,
    );
    const actorFrames = new Map<string, Array<{ atMs: number; source: string }>>();
    for (const initial of session.initialFrames) {
      actorFrames.set(initial.actor, [{ atMs: 0, source: initial.source }]);
    }
    for (const event of events) {
      const frames = actorFrames.get(event.actor!) ?? [];
      const atMs = Math.max(0, Math.min(durationMs, event.audioMs ?? 0));
      if (frames.at(-1)?.source !== event.source) {
        frames.push({ atMs, source: event.source! });
      }
      actorFrames.set(event.actor!, frames);
    }

    const speakerName = fileName(session.source).toLowerCase().includes("winston")
      ? "WINSTON"
      : "ASHER";
    const speakerFrames = Array.from(actorFrames.entries())
      .find(([actor]) => shortActor(actor) === speakerName)?.[1] ?? [];
    const parsedSpeakerFrames = speakerFrames.map((frame) => ({
      ...frame,
      state: parseCell(frame.source),
    }));
    const speakerPoseChanges = parsedSpeakerFrames.filter((frame) => frame.state.pose !== "neutral").length;
    const speakerBlinks = parsedSpeakerFrames.filter((frame) => frame.state.blink !== "open").length;
    const speakerMouthTransitions = parsedSpeakerFrames.filter((frame, index) =>
      index === 0 || frame.state.mouth !== parsedSpeakerFrames[index - 1].state.mouth,
    ).length;
    const firstWordMs = Math.round((receipt.words[0]?.start ?? 0) * 1000);
    const firstSpeakingMouthMs = parsedSpeakerFrames.find((frame) => frame.state.mouth !== "rest")?.atMs ?? durationMs;
    const samples = state.samples.filter((sample) =>
      sample.audioSource === session.source &&
      sample.wallMs >= session.startedWallMs &&
      sample.wallMs <= endedWallMs,
    );
    const progressingSamples = samples.filter((sample) => sample.audioMs > 0);
    const maximumObservationGap = progressingSamples.length > 1
      ? Math.max(...progressingSamples.slice(1).map((sample, index) =>
        sample.wallMs - progressingSamples[index].wallMs,
      ))
      : 0;
    // Supported expression is expected; retain timing as the diagnostic criterion.
    const pass = Math.abs(firstSpeakingMouthMs - firstWordMs) <= 120 &&
      maximumObservationGap <= 90;

    const width = 1000;
    const left = 96;
    const trackWidth = width - left - 18;
    const actorEntries = Array.from(actorFrames.entries());
    const height = 82 + actorEntries.length * 48;
    const x = (milliseconds: number): number => left +
      Math.max(0, Math.min(1, milliseconds / durationMs)) * trackWidth;
    const wordSvg = receipt.words.map((word, index) => {
      const start = x(word.start * 1000);
      const end = x(word.end * 1000);
      const widthPx = Math.max(2, end - start - 1);
      const fill = index % 2 === 0 ? "#193f54" : "#254c62";
      const label = widthPx >= 38
        ? `<text x="${start + 4}" y="29" class="qa-word">${escapeXml(word.text)}</text>`
        : "";
      return `<rect x="${start}" y="8" width="${widthPx}" height="28" rx="3" fill="${fill}"/>${label}`;
    }).join("");
    const actorSvg = actorEntries.map(([actor, frames], actorIndex) => {
      const y = 54 + actorIndex * 48;
      const segments = frames.map((frame, index) => {
        const nextAt = frames[index + 1]?.atMs ?? durationMs;
        const state = parseCell(frame.source);
        const start = x(frame.atMs);
        const end = x(nextAt);
        const widthPx = Math.max(1, end - start);
        const outline = state.pose !== "neutral"
          ? "#ff4d6d"
          : state.blink !== "open"
            ? "#ff9f43"
            : "#09111f";
        const label = widthPx >= 48 ? escapeXml(state.mouth) : "";
        return `<rect x="${start}" y="${y}" width="${widthPx}" height="30" fill="${mouthColor(state.mouth)}" stroke="${outline}" stroke-width="${state.pose !== "neutral" || state.blink !== "open" ? 3 : 1}"/><text x="${start + 3}" y="${y + 20}" class="qa-cell">${label}</text>`;
      }).join("");
      return `<text x="8" y="${y + 20}" class="qa-actor">${escapeXml(shortActor(actor))}</text>${segments}`;
    }).join("");

    let panel = document.querySelector<HTMLElement>("#awkwardpixels-playback-report");
    if (!panel) {
      panel = document.createElement("section");
      panel.id = "awkwardpixels-playback-report";
      panel.setAttribute("aria-label", "Lip-sync playback QA");
      panel.style.cssText = "position:fixed;z-index:99999;left:12px;right:12px;bottom:12px;max-height:48vh;overflow:auto;background:#08111fef;color:#f9f1d0;border:2px solid #56e0cf;box-shadow:6px 6px 0 #ff77a8;padding:10px;font:12px/1.35 ui-monospace,monospace;text-align:left;";
      document.body.appendChild(panel);
    }
    const card = document.createElement("article");
    card.style.cssText = "border-top:1px solid #34465f;padding:8px 0 12px;";
    card.innerHTML = `
      <header style="display:flex;gap:12px;align-items:baseline;flex-wrap:wrap">
        <strong style="color:${pass ? "#56e0cf" : "#ff6b7a"}">${pass ? "PASS" : "REVIEW"}</strong>
        <b>${escapeXml(fileName(session.source))}</b>
        <span>words ${receipt.words.length}</span>
        <span>mouth changes ${speakerMouthTransitions}</span>
        <span>speaker poses ${speakerPoseChanges}</span>
        <span>speaker blinks ${speakerBlinks}</span>
        <span>max audit gap ${Math.round(maximumObservationGap)} ms</span>
      </header>
      <svg viewBox="0 0 ${width} ${height}" style="width:100%;min-width:720px;height:auto;margin-top:6px;background:#0d1a2c" role="img" aria-label="Words and selected character cells aligned to the audio clock">
        <style>.qa-word{fill:#f9f1d0;font:13px ui-monospace,monospace}.qa-cell{fill:#08111f;font:12px ui-monospace,monospace}.qa-actor{fill:#f9f1d0;font:bold 13px ui-monospace,monospace}</style>
        ${wordSvg}${actorSvg}
      </svg>`;
    panel.prepend(card);
  };

  const originalDrawImage = CanvasRenderingContext2D.prototype.drawImage;
  const invokeDrawImage = originalDrawImage as unknown as (
    this: CanvasRenderingContext2D,
    image: CanvasImageSource,
    ...args: number[]
  ) => void;
  CanvasRenderingContext2D.prototype.drawImage = function drawImage(
    this: CanvasRenderingContext2D,
    image: CanvasImageSource,
    ...args: number[]
  ): void {
    const source = image instanceof HTMLImageElement
      ? image.currentSrc || image.src
      : "";
    const canvas = this.canvas;
    if (source && frameByCanvas.get(canvas) !== source) {
      frameByCanvas.set(canvas, source);
      const media = playingMedia();
      addEvent({
        type: "frame",
        wallMs: wallMs(),
        audioMs: media ? Math.round(media.currentTime * 1000) : undefined,
        source,
        actor: canvas.getAttribute("aria-label") ||
          canvas.closest(".actor")?.className ||
          "unknown",
      });
    }
    invokeDrawImage.call(this, image, ...args);
  } as typeof CanvasRenderingContext2D.prototype.drawImage;

  const originalPlay = HTMLMediaElement.prototype.play;
  HTMLMediaElement.prototype.play = function play(): Promise<void> {
    activeMedia.add(this);
    const source = this.currentSrc || this.src;
    const session: PlaybackSession = {
      source,
      startedWallMs: wallMs(),
      eventStartIndex: state.events.length,
      initialFrames: Array.from(frameByCanvas.entries()).map(([canvas, frameSource]) => ({
        actor: canvas.getAttribute("aria-label") ||
          canvas.closest(".actor")?.className ||
          "unknown",
        source: frameSource,
      })),
      receipt: loadReceipt(source),
    };
    playbackSessions.set(this, session);
    addEvent({
      type: "play-requested",
      wallMs: wallMs(),
      audioMs: Math.round(this.currentTime * 1000),
      source: this.currentSrc || this.src,
    });
    this.addEventListener("playing", () => addEvent({
      type: "playing",
      wallMs: wallMs(),
      audioMs: Math.round(this.currentTime * 1000),
      source: this.currentSrc || this.src,
    }), { once: true });
    this.addEventListener("ended", () => {
      addEvent({
        type: "ended",
        wallMs: wallMs(),
        audioMs: Math.round(this.currentTime * 1000),
        source: this.currentSrc || this.src,
      });
      activeMedia.delete(this);
      const completed = playbackSessions.get(this);
      if (completed) void renderPlaybackReport(this, completed);
    }, { once: true });
    return originalPlay.call(this);
  };

  const attachCaptionObserver = (): void => {
    const captionElement = document.querySelector(".caption-deck p");
    if (!captionElement) {
      window.setTimeout(attachCaptionObserver, 50);
      return;
    }
    caption = captionElement.textContent || "";
    new MutationObserver(() => {
      caption = captionElement.textContent || "";
      addEvent({ type: "caption", wallMs: wallMs(), caption });
    }).observe(captionElement, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  };
  attachCaptionObserver();

  window.setInterval(() => {
    const media = playingMedia();
    if (!media) return;
    state.samples.push({
      wallMs: wallMs(),
      audioMs: Math.round(media.currentTime * 1000),
      audioSource: media.currentSrc || media.src,
      caption,
      frames: Array.from(frameByCanvas.entries()).map(([canvas, source]) => ({
        actor: canvas.getAttribute("aria-label") ||
          canvas.closest(".actor")?.className ||
          "unknown",
        source,
      })),
    });
    if (state.samples.length > 12000) state.samples.shift();
    publish();
  }, 50);

  publish();
}
